import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { handleSecureError } from '@/utils/secureErrorHandler';
import { SessionRecord } from '@/types/session';
import { sessionHistoryKeys } from './queries/sessionHistoryKeys';
import { useMemo, useEffect } from 'react';
import { apiClient } from '@/utils/apiClient';

export const useSessionHistory = (categoryId?: string, pageSize = 50) => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery({
    // Use user ID directly without fallback to avoid cache key conflicts
    queryKey: user?.id ? sessionHistoryKeys.list(user.id, categoryId, pageSize) : ['sessionHistory', 'disabled'],
    queryFn: async ({ pageParam = 0 }) => {
      console.log('[useSessionHistory] Fetching page:', {
        pageParam,
        userId: user?.id,
        categoryId,
        authLoading
      });

      if (!user?.id) {
        console.log('[useSessionHistory] No user ID, returning empty');
        return { sessions: [], count: 0, nextOffset: 0 };
      }

      // Build query parameters
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: pageParam.toString(),
        include_count: 'true'
      });

      if (categoryId) {
        params.append('category_id', categoryId);
      }

      try {
        // Call API endpoint
        const response = await apiClient.get(`/meditation/sessions?${params.toString()}`);

        console.log('[useSessionHistory] API Response:', {
          sessionCount: response.sessions?.length || 0,
          totalCount: response.count,
          offset: pageParam,
          limit: pageSize
        });

        // Validate response structure
        if (!response || typeof response !== 'object') {
          console.error('[useSessionHistory] Invalid response format:', response);
          throw new Error('Invalid response format from server');
        }

        if (!Array.isArray(response.sessions)) {
          console.error('[useSessionHistory] Sessions is not an array:', response.sessions);
          throw new Error('Sessions data is not in expected format');
        }

        return {
          sessions: response.sessions as SessionRecord[],
          count: response.count || 0,
          nextOffset: pageParam + pageSize
        };
      } catch (error) {
        console.error('[useSessionHistory] Error fetching sessions:', error);
        handleSecureError(error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextOffset >= lastPage.count) return undefined;
      return lastPage.nextOffset;
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    retry: false, // Disable retry to avoid stale cache issues
    refetchOnMount: true, // Always fetch fresh data on mount
  });

  // Flatten all pages into a single array
  const sessions = useMemo(() => {
    if (!data?.pages) {
      console.log('[useSessionHistory] No pages data available');
      return [];
    }

    const allSessions = data.pages.flatMap(page => page.sessions || []);
    console.log('[useSessionHistory] Total sessions across all pages:', allSessions.length);
    return allSessions;
  }, [data]);

  const totalCount = data?.pages?.[0]?.count || 0;

  // Derive favorites from main query (no separate query needed)
  const favoritesSessions = useMemo(() =>
    sessions.filter(session => session.is_favorite),
    [sessions]
  );

  // Group sessions by category
  const sessionsByCategory = useMemo(() =>
    sessions.reduce((acc, session) => {
      if (!acc[session.category_id]) {
        acc[session.category_id] = [];
      }
      acc[session.category_id].push(session);
      return acc;
    }, {} as Record<string, SessionRecord[]>),
    [sessions]
  );

  // Recent sessions (last 7 days)
  const recentSessions = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return sessions.filter(session => {
      const sessionDate = new Date(session.created_at);
      return sessionDate > weekAgo;
    });
  }, [sessions]);

  // Log overall state for debugging
  console.log('[useSessionHistory] Hook state:', {
    authLoading,
    isLoading,
    hasError: !!error,
    sessionCount: sessions.length,
    totalCount,
    hasMore: hasNextPage,
    pagesLoaded: data?.pages?.length || 0,
    userId: user?.id
  });

  return {
    sessions,
    favoritesSessions,
    sessionsByCategory,
    recentSessions,
    totalCount,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    isLoadingMore: isFetchingNextPage,
    isLoading: authLoading || isLoading, // Include auth loading state
    error,
    refetch
  };
};